import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/axios';

export function useOrganisations() {
  return useQuery({
    queryKey: ['organisations'],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get('/api/organisations', { signal });
      return data ?? [];
    },
    staleTime: 5 * 60_000,
    retry: false,
    throwOnError: false,
  });
}

export function useOrganisation(id) {
  return useQuery({
    queryKey: ['organisations', id],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get(`/api/organisations/${id}`, { signal });
      return data;
    },
    enabled: !!id,
    staleTime: 2 * 60_000,
  });
}

export function useOrganisationMembers(id) {
  return useQuery({
    queryKey: ['organisations', id, 'members'],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get(`/api/organisations/${id}/members`, { signal });
      return data ?? [];
    },
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCreateOrganisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await apiClient.post('/api/organisations', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['organisations'] });
    },
  });
}

export function useAddOrgMember(orgId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await apiClient.post(`/api/organisations/${orgId}/members`, payload);
      return data;
    },
    onSuccess: () => {
      qc.refetchQueries({ queryKey: ['organisations', orgId, 'members'] });
      qc.refetchQueries({ queryKey: ['organisations', orgId] });
    },
  });
}

export function useRemoveOrgMember(orgId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (uid) => {
      await apiClient.delete(`/api/organisations/${orgId}/members/${uid}`);
    },
    onSuccess: () => {
      qc.refetchQueries({ queryKey: ['organisations', orgId, 'members'] });
      qc.refetchQueries({ queryKey: ['organisations', orgId] });
    },
  });
}
